"use client";

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Module } from '@/types/module';
import { moduleService } from '@/utils/services/module';
import modulesService from '@/templates/Admin/variants/default/utils/services/module';
import { useIdPath } from '@/contexts/IdPathProvider';
import { RESOURCE_MODULES_SUGGESTIONS } from '@/config/resourceModules';

const DEFAULT_MODULES_TO_SEED = [
    { name: 'minimap', display_name: 'Bản đồ Mini', description: 'Hiển thị chức năng Bản đồ Mini ở trang người dùng', for_admin: 1, for_user: 1 },
    { name: 'map3d', display_name: 'Bản đồ số hóa 3D (StarGlobal Map 3D)', description: 'Nhúng bản đồ số hóa 3D thay cho Minimap 2D thông thường', for_admin: 0, for_user: 0 },
    { name: 'odoo', display_name: 'Cấu hình module Odoo', description: 'Hiển thị menu Odoo ERP ở trang admin', for_admin: 0, for_user: 0 },
    { name: 'bim3d', display_name: '3D BIM', description: 'Phân hệ 3D BIM: model kỹ thuật, thuộc tính thành phần và timeline tiến độ', for_admin: 0, for_user: 0 },
    { name: 'model-configurator', display_name: 'Model Configurator', description: 'Đổi màu, texture và vật liệu từng thành phần của model 3D (IFC, FBX, OBJ, GLTF)', for_admin: 0, for_user: 0 },
    { name: 'contractorManagement', display_name: 'Quản lý nhà thầu (ERP)', description: 'Nhúng iframe hệ thống Quản lý nhà thầu (CSMS)', for_admin: 0, for_user: 0 },
    { name: 'customMenu', display_name: 'Cấu hình menu tùy chỉnh', description: 'Hiển thị menu tùy chỉnh ở trang admin', for_admin: 0, for_user: 0 },
    { name: 'iotMenu', display_name: 'Menu thiết bị IoT', description: 'Hiển thị menu IoT ở trang admin', for_admin: 0, for_user: 0 },
    { name: 'product', display_name: 'Quản lý sản phẩm', description: 'Hiển thị menu Sản phẩm ở trang người dùng', for_admin: 0, for_user: 0 },
    { name: 'sessionPolicy', display_name: 'Quản lý phiên đăng nhập', description: 'Cho phép tùy chỉnh thời gian sống của token và thời gian timeout của phiên đăng nhập', for_admin: 0, for_user: 0 },
    { name: 'auditLog', display_name: 'Lịch sử hệ thống', description: 'Cho phép xem nhật ký hoạt động, sao lưu phục hồi và lịch trình dọn dẹp', for_admin: 1, for_user: 0 },
    { name: 'fileManagement', display_name: 'Quản lý file', description: 'Bật tắt hiển thị menu Quản lý file trên Sidebar Admin', for_admin: 1, for_user: 0 },
    { name: 'pointCloudViewer', display_name: 'Quản lý Point Cloud', description: 'Bật tắt hiển thị menu Quản lý Point Cloud trên Sidebar Admin', for_admin: 0, for_user: 0 },
    { name: 'menuManagement', display_name: 'Quản lý hiển thị Menu', description: 'Cho phép tùy biến cây menu phân cấp, sắp xếp thứ tự và cấu hình các module hiển thị trên thanh Sidebar Admin của dự án', for_admin: 1, for_user: 0 },
    { name: 'permissionGroupManagement', display_name: 'Quản lý nhóm quyền', description: 'Cho phép quản lý và phân quyền cho các nhóm người dùng trong dự án', for_admin: 1, for_user: 0 },
    { name: 'approvalManagement', display_name: 'Phê duyệt tài khoản', description: 'Cho phép phê duyệt tài khoản người dùng đăng ký mới trong dự án', for_admin: 1, for_user: 0 },
    { name: 'aiInteractionRecording', display_name: 'Ghi hình tương tác AI Agent', description: 'Cho phép người dùng ghi hình màn hình tương tác trực tiếp với trợ lý ảo AI Agent 3D, thu âm giọng nói hai chiều và chia sẻ video MP4.', for_admin: 1, for_user: 1 },
    { name: 'agentChatInput', display_name: 'Thanh gõ phím AI Agent (Dual Input)', description: 'Hiển thị thanh gõ chữ nổi trên 360 Tour cho phép khách tham quan vừa nói vừa gõ phím với trợ lý ảo', for_admin: 0, for_user: 0 }
];

const ALL_MODULES_TO_SEED = [
    ...DEFAULT_MODULES_TO_SEED,
    ...RESOURCE_MODULES_SUGGESTIONS.map(m => ({ ...m, for_admin: (m as any).for_admin ?? 0, for_user: (m as any).for_user ?? 0 }))
];

interface ModuleContextType {
    activeModules: Record<string, Module>;
    activeModulesAdmin: Record<string, Module>;
    refreshModules: () => Promise<void>;
    isFallback?: boolean;
}

const ModuleContext = createContext<ModuleContextType | null>(null);

const normalizeActiveModules = (modulesData: any, scopeField: 'for_admin' | 'for_user'): Record<string, Module> => {
    if (!modulesData) return {};

    if (Array.isArray(modulesData)) {
        const mapped = modulesData.reduce((acc, moduleItem: any) => {
            const moduleName = moduleItem?.name || moduleItem?.name_module;
            const activeValue = Number(moduleItem?.[scopeField] ?? moduleItem?.active ?? 0);

            if (moduleName && activeValue === 1) {
                acc[moduleName] = { ...moduleItem, active: '1' };
            }

            return acc;
        }, {} as Record<string, Module>);

        // Nếu dự án chưa từng auto-seed 'aiInteractionRecording' trong DB, mặc định bật cho User;
        // khi Admin đã tắt (tồn tại trong modulesData với active = 0), mapped['aiInteractionRecording'] sẽ là undefined (Tắt).
        if (
            scopeField === 'for_user' &&
            !modulesData.some((m: any) => (m?.name || m?.name_module) === 'aiInteractionRecording')
        ) {
            mapped['aiInteractionRecording'] = {
                id: 'default-ai-interaction-recording',
                name: 'aiInteractionRecording',
                active: '1',
                for_user: 1,
                for_admin: 1
            } as any;
        }

        return mapped;
    }

    if (typeof modulesData === 'object') {
        return Object.entries(modulesData).reduce((acc, [moduleName, moduleValue]: [string, any]) => {
            const activeValue = Number(moduleValue?.[scopeField] ?? moduleValue?.active ?? 0);

            if (moduleName && activeValue === 1) {
                acc[moduleName] = { ...moduleValue, active: '1' };
            }

            return acc;
        }, {} as Record<string, Module>);
    }

    return {};
};

export function ModuleProvider({ children }: { children: React.ReactNode}) {
    const [activeModules, setActiveModules] = useState<Record<string, Module>>({});
    const [activeModulesAdmin, setActiveModulesAdmin] = useState<Record<string, Module>>({});
    const [moduleList, setModuleList] = useState<Module[]>([]);
    const { idPath } = useIdPath();
    const hasAutoSeeded = React.useRef(false);

    const autoSeedModules = useCallback(async () => {
        if (!idPath || hasAutoSeeded.current) return;
        hasAutoSeeded.current = true;
        
        try {
            const res = await moduleService.getAllModules({ id_path: idPath });
            const allModuleData = res?.data || [];
            
            const missingModules = ALL_MODULES_TO_SEED.filter(
                rm => !allModuleData.some((em: any) => em.name === rm.name)
            );

            if (missingModules.length > 0) {
                await Promise.all(missingModules.map(async (m) => {
                    await modulesService.createModule(idPath, { ...m, active: 1, auto_seed: true });
                }));
            }
        } catch (error) {
            console.error('Error auto-seeding modules:', error);
            hasAutoSeeded.current = false; // Allow retry if failed completely
        }
    }, [idPath]);

    const fetchModules = useCallback(async () => {
        if (!idPath) return;
        try {
            const modules = await moduleService.getModuleForUser({ id_path: idPath });
            setActiveModules(normalizeActiveModules(modules.data, 'for_user'));
        } catch (error) {
            console.error('Error fetching modules:', error);
        }
    }, [idPath]);

    const fetchModulesAdmin = useCallback(async () => {
        if (!idPath) return;
        try {
            const modules = await moduleService.getModuleForAdmin({ id_path: idPath });
            setActiveModulesAdmin(normalizeActiveModules(modules.data, 'for_admin'));
        } catch (error) {
            console.error('Error fetching modules:', error);
        }
    }, [idPath]);

    const refreshModules = useCallback(async () => {
        await Promise.all([fetchModules(), fetchModulesAdmin()]);
    }, [fetchModules, fetchModulesAdmin]);

    useEffect(() => {
        autoSeedModules().then(() => {
            fetchModules();
            fetchModulesAdmin();
        });
    }, [autoSeedModules, fetchModules, fetchModulesAdmin]);

    return (
        <ModuleContext.Provider value={{ activeModules, activeModulesAdmin, refreshModules, isFallback: false }}>
            {children}
        </ModuleContext.Provider>
    );
}

export const useModules = () => {
    const context = useContext(ModuleContext);
    if (!context) {
        return { activeModules: {}, activeModulesAdmin: {}, refreshModules: async () => {}, isFallback: true };
    }
    return context;
}
